import { ref } from 'vue';
import type { ArrayElementType } from '@/core/types/gengeric';
import type {
  AddLegalExistingMutationVariables,
  AddLegalMutationVariables,
  FindFreeLegalQueryVariables,
  GetIsLegalFreeQueryVariables,
  GetLegalQuery,
  GetLegalsQuery,
  SaveLegalEdiTypeMutationVariables,
} from '@/graphql/generated';

import { emptyReplicasList, fillReplicasList, type WithReplica } from '@/core/utils/store/replicas';
import {
  useAddLegalExistingMutation,
  useAddLegalMutation,
  useFindFreeLegalQuery,
  useGetIsLegalFreeQuery,
  useGetLegalQuery,
  useGetLegalsQuery,
  useSaveLegalEdiTypeMutation,
} from '@/graphql/generated';

export type LegalsType = GetLegalsQuery['legals'];
export type LegalsListItemType = ArrayElementType<LegalsType['legals']>;
export type LegalType = GetLegalQuery['legal'];

const legals = ref<WithReplica<LegalsType, 'legals', LegalsListItemType>>({
  legals: emptyReplicasList(),
});

const activeLegal = ref<LegalType>();

export const useLegalsStore = () => {
  const getLegals = async () => {
    const { request, data, errors, response } = useGetLegalsQuery({});

    await request();

    if (!errors.value && data.value) {
      legals.value = {
        ...data.value.legals,
        legals: fillReplicasList(data.value.legals.legals, legals.value?.legals?.tempItem, 'cid'),
      };
    }

    return response;
  };

  const getLegal = async (legalCid: string) => {
    activeLegal.value = undefined;

    const { request, data, errors, response } = useGetLegalQuery({ legalCid });

    await request();

    if (!errors.value && data.value) {
      activeLegal.value = data.value.legal;
    }

    return response;
  };

  const getLegalIsFree = async (payload: GetIsLegalFreeQueryVariables['input']) => {
    const { request } = useGetIsLegalFreeQuery({ input: payload });

    const response = await request();

    return response;
  };

  const findFreeLegal = async (payload: FindFreeLegalQueryVariables['input']) => {
    const { request } = useFindFreeLegalQuery({ input: payload });

    const response = await request();

    return response;
  };

  const addLegal = async (payload: AddLegalMutationVariables['input']) => {
    const { request } = useAddLegalMutation({ input: payload });

    const response = await request();

    return response;
  };

  const addLegalExisting = async (payload: AddLegalExistingMutationVariables['input']) => {
    const { request } = useAddLegalExistingMutation({ input: payload });

    const response = await request();

    return response;
  };

  const saveLegalEDIType = async (payload: SaveLegalEdiTypeMutationVariables) => {
    const { request } = useSaveLegalEdiTypeMutation(payload);

    const response = await request();

    if (response?.data?.saveLegalEDIType && activeLegal.value) {
      activeLegal.value.data.ediType = payload.ediType;
    }

    return response;
  };

  return {
    legals,
    activeLegal,

    getLegals,
    getLegal,
    getLegalIsFree,
    findFreeLegal,
    addLegal,
    addLegalExisting,
    saveLegalEDIType,
  };
};
